import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button, Checkbox, MessageBar, MessageBarActions, MessageBarBody, MessageBarTitle, Text, tokens } from "@fluentui/react-components";
import { PeopleAdd16Regular, Send16Regular } from "@fluentui/react-icons";
import { api, type ClientSummary } from "../lib/api.ts";
import { ImportClientsDialog } from "./ImportClients.tsx";
import { HeadsUp } from "./HeadsUp.tsx";
import { ConfirmButton } from "./ConfirmButton.tsx";

const fmtDay = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" }) : "");

/**
 * Where the trainer's client list stands, on the home page: nobody yet → import; some invited but not joined →
 * "x of y joined" with a reminder; and, until the first week has been planned, the offer to plan it now —
 * once the clients have been told to expect RackUp.
 */
export function ClientProgress({ trainerFirst, justSetUp }: { trainerFirst: string; justSetUp: boolean }) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["clients-summary"], queryFn: () => api.get<ClientSummary>("/api/clients/summary"), refetchInterval: 30_000 });
  const [importOpen, setImportOpen] = useState(false);
  const [told, setTold] = useState(false);
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ["clients-summary"] });
    void qc.invalidateQueries({ queryKey: ["schedule"] });
    void qc.invalidateQueries({ queryKey: ["availability"] });
  };
  const remind = useMutation({ mutationFn: () => api.post<{ reminded: number }>("/api/clients/remind-invites"), onSuccess: refresh });
  const planNow = useMutation({
    mutationFn: async (which: "this" | "next") => {
      if (!d?.clientsToldAt) await api.post("/api/clients/clients-told");
      return api.post<{ sent: number; skipped: number; weekStart: string }>("/api/schedule/plan-now", { which });
    },
    onSuccess: refresh,
  });
  const d = q.data;
  if (!d) return null;

  const dialog = <ImportClientsDialog open={importOpen} onClose={() => setImportOpen(false)} trainerFirst={trainerFirst} rackupNumber={d.rackupNumber} />;
  const importBtn = (primary = false) => (
    <Button size="small" appearance={primary ? "primary" : "secondary"} icon={<PeopleAdd16Regular />} onClick={() => setImportOpen(true)}>
      {d.total ? "Import more" : "Import clients"}
    </Button>
  );

  // The dialog keeps the same place in the tree whichever state the card is in, so an import in progress isn't reset.
  return (
    <>
      {dialog}
      <ProgressBody d={d} justSetUp={justSetUp} trainerFirst={trainerFirst} importBtn={importBtn} told={told} setTold={setTold} remind={remind} planNow={planNow} />
    </>
  );
}

function ProgressBody({ d, justSetUp, trainerFirst, importBtn, told, setTold, remind, planNow }: { d: ClientSummary; justSetUp: boolean; trainerFirst: string; importBtn: (primary?: boolean) => React.ReactNode; told: boolean; setTold: (v: boolean) => void; remind: ReturnType<typeof useMutation<{ reminded: number }, Error, void>>; planNow: ReturnType<typeof useMutation<{ sent: number; skipped: number; weekStart: string }, Error, "this" | "next">> }) {
  // Nobody yet: the one thing to do next.
  if (d.total === 0) {
    return (
      <MessageBar intent={justSetUp ? "success" : "info"} layout="multiline" style={{ marginBottom: 12 }}>
        <MessageBarBody>
          <MessageBarTitle>{justSetUp ? "Your week is set up. Next: your clients." : "Add your clients"}</MessageBarTitle>
          Paste their names and numbers and RackUp invites each one on your behalf — or share your sign-up link above. Hours change? Just tell RackUp below.
        </MessageBarBody>
        <MessageBarActions>{importBtn(true)}</MessageBarActions>
      </MessageBar>
    );
  }

  const pending = d.notJoined;
  const canPlan = d.joined > 0 && !d.everPlanned;
  const today = new Date().getDay(); // 0 = Sun
  const thisWeekLeft = today >= 1 && today <= 4; // Mon–Thu: still worth planning the rest of this week

  return (
    <>
      {pending.length > 0 && (
        <MessageBar intent={d.joined === 0 ? "warning" : "info"} layout="multiline" style={{ marginBottom: 12 }}>
          <MessageBarBody>
            <MessageBarTitle>
              {d.joined} of {d.total} clients joined
            </MessageBarTitle>
            Still to join: {pending.map((c) => c.name.split(" ")[0]).join(", ")}. They've each had a WhatsApp from RackUp with their own link{pending.some((c) => c.remindedAt) ? ", and a reminder" : ""}. Clients aren't asked about any week until they've joined.
            {remind.data && <Text block size={200} style={{ marginTop: 4 }}>{remind.data.reminded ? `Reminded ${remind.data.reminded}.` : "Nobody due a reminder yet."}</Text>}
          </MessageBarBody>
          <MessageBarActions>
            {importBtn()}
            <ConfirmButton size="small" appearance="primary" icon={<Send16Regular />} disabled={d.canRemind === 0 || remind.isPending} title="Send reminders?" message={`RackUp sends one more WhatsApp, on your behalf, to the ${d.canRemind} client${d.canRemind === 1 ? "" : "s"} invited over 48 hours ago who haven't joined.`} confirmLabel="Send" onConfirm={() => remind.mutate()}>
              {d.canRemind ? `Remind ${d.canRemind}` : "Remind (48h after invite)"}
            </ConfirmButton>
          </MessageBarActions>
        </MessageBar>
      )}

      {canPlan && (
        <MessageBar intent="success" layout="multiline" style={{ marginBottom: 12 }}>
          <MessageBarBody>
            <MessageBarTitle>{justSetUp ? "Your week is set up. " : ""}Want me to plan {thisWeekLeft ? "the rest of this week" : "next week"} now?</MessageBarTitle>
            Rather than waiting for your planning day, RackUp can ask your {d.joined} joined client{d.joined === 1 ? "" : "s"} right away.
            <b> Before it does, your clients need to know a message from RackUp is coming</b> — on your behalf — or it looks like spam and gets ignored. Send them this first:
            <div style={{ marginTop: 8 }}>
              <HeadsUp trainerFirst={trainerFirst} rackupNumber={d.rackupNumber} compact={!!d.clientsToldAt} />
            </div>
            {!d.clientsToldAt && <Checkbox checked={told} onChange={(_, v) => setTold(!!v.checked)} label="I've sent this (or told my clients another way)." />}
            {d.clientsToldAt && <Text size={200} block style={{ color: tokens.colorNeutralForeground3, marginTop: 4 }}>You confirmed your clients were told on {fmtDay(d.clientsToldAt)}.</Text>}
            {planNow.data && <Text size={200} block style={{ marginTop: 4 }}>Asked {planNow.data.sent} client{planNow.data.sent === 1 ? "" : "s"} about w/c {planNow.data.weekStart}{planNow.data.skipped ? ` (${planNow.data.skipped} on usual times, not asked)` : ""}.</Text>}
            {planNow.error && <Text size={200} block style={{ color: tokens.colorPaletteRedForeground1, marginTop: 4 }}>{(planNow.error as Error).message}</Text>}
          </MessageBarBody>
          <MessageBarActions>
            {thisWeekLeft && (
              <ConfirmButton size="small" appearance="primary" disabled={(!d.clientsToldAt && !told) || planNow.isPending} title="Plan the rest of this week?" message="RackUp asks every joined client now about the days left this week (from tomorrow), with a day to reply, then builds the schedule. This week's hours lock while it asks." confirmLabel="Ask clients now" onConfirm={() => planNow.mutate("this")}>
                Plan the rest of this week
              </ConfirmButton>
            )}
            <ConfirmButton size="small" appearance={thisWeekLeft ? "secondary" : "primary"} disabled={(!d.clientsToldAt && !told) || planNow.isPending} title="Plan next week now?" message="RackUp asks every joined client now about next week, instead of on your planning day. Next week's hours lock while it asks." confirmLabel="Ask clients now" onConfirm={() => planNow.mutate("next")}>
              Plan next week
            </ConfirmButton>
          </MessageBarActions>
        </MessageBar>
      )}
    </>
  );
}
